<template>
  <!-- LIST VIEW -->
  <li
    class="board-card board-card--list"
    v-if="layout === 'list'"
  >
    <RouterLink
      :to="linkTo"
      class="board-card__board-link"
    >
      <div class="board-card__board-preview">
        <div
          class="board-card__board-preview-image"
          :style="{
            backgroundImage: `url(${thumbnail})`,
          }"
        />
      </div>
      <div class="board-card__body">
        <div class="board-card__type">
          {{ type }}
        </div>
        <div class="board-card__title">
          {{ decodedBoardName }}
        </div>
        <div class="board-card__meta-wrapper">
          <p class="board-card__meta">
            <span>{{ $t('Created') }}</span>
            {{ created }}
          </p>
          <p class="board-card__meta">
            <span>{{ $t('Updated') }}</span>
            {{ updated }}
          </p>
        </div>
      </div>
    </RouterLink>
    <BoardListingOptions
      v-if="showBoardListingOptions"
      :board="board"
      :layout="layout"
      :sidebar-filter="sidebarFilter"
    />
  </li>

  <!-- GRID VIEW -->
  <NebulaCard
    v-else
    class="board-card board-card--grid"
  >
    <template #thumbnail>
      <RouterLink
        class="board-card__board-preview"
        :to="linkTo"
      >
        <div
          class="board-card__board-preview-image"
          :style="{
            backgroundImage: `url(${thumbnail})`,
          }"
        />
      </RouterLink>
    </template>
    <template #content>
      <NebulaCardHeader
        :asset-type="type"
      >
        <template #actionsMenu>
          <BoardListingOptions
            v-if="showBoardListingOptions"
            :board="board"
            :layout="layout"
            :sidebar-filter="sidebarFilter"
          />
        </template>
      </NebulaCardHeader>
      <!-- NebulaCardTitle does not allow for router links so we can't use it here -->
      <RouterLink
        class="nebula-card__title"
        :to="linkTo"
      >
        {{ decodedBoardName }}
      </RouterLink>
      <NebulaCardDescription
        :text="sidebarFilter === 'TRASH' ? deleted : created"
      />
    </template>
  </NebulaCard>
</template>

<script>
import {
  NebulaCard,
  NebulaCardDescription,
  NebulaCardHeader,
} from '@discoveryedu/nebula-components';
import { get } from 'lodash-es';
import BoardListingOptions from '@/components/home/BoardListingOptions.vue';
import defaultThumbnail from '@/assets/blank_board.png';
import { decodeHTML, formatDate } from '@/lib/utils';

export default {
  name: 'BoardListing',
  components: {
    BoardListingOptions,
    NebulaCard,
    NebulaCardDescription,
    NebulaCardHeader,
  },
  props: {
    board: {
      type: Object,
      required: true,
    },
    layout: {
      type: String,
      required: true,
    },
    sidebarFilter: {
      type: String,
      required: true,
    },
  },
  computed: {
    created() {
      if (!this.board.metadata || !this.board.metadata.created_on) return '';

      return formatDate(this.board.metadata.created_on, true);
    },
    decodedBoardName() {
      if (!this.board.name) {
        // If there's no board name, show a default string
        if (this.board.options.type === 'lesson') {
          return this.$t('New Lesson');
        }

        return this.board.options.type === 'board' ? this.$t('New Board') : this.$t('New Slideshow');
      }

      // Board names are escaped using HTML entities on the BE. Decode them (e.g. &amp; => &)
      return decodeHTML(this.board.name);
    },
    formattedBoard() {
      return {
        id: this.board.id,
        description: this.updated,
        title: this.decodedBoardName,
      };
    },
    linkTo() {
      const isAssignedTab = this.sidebarFilter === 'ASSIGNED';

      // We should always have the user navigate using the pointer asset so that they get the
      // most recent version of a board, UNLESS it is one that has been assigned. In that case,
      // they should get the exact revision that was assigned.
      const assetReference = this.board.references.find((ref) => (
        ref.reference_type === 'asset' && (
          isAssignedTab
            ? ref.reference_subtype !== 'head'
            : ref.reference_subtype === 'head'
        )
      ));

      if (this.sidebarFilter === 'TRASH') {
        return {
          name: 'view',
          query: {
            id: get(assetReference, 'reference_id'),
            dataType: this.board.state,
          },
        };
      }

      return {
        name: ['admin', 'owner', 'edit'].includes(this.board.access_level) && !isAssignedTab
          ? 'edit' : 'view',
        query: {
          id: assetReference ? assetReference.reference_id : this.board.id,
          // See note above; use_revision allows a teacher to see the exact revision
          // that was assigned
          use_revision: isAssignedTab ? 1 : undefined,
        },
      };
    },
    updated() {
      if (!this.board.metadata || !this.board.metadata.updated_on) return this.$t('Never');

      return formatDate(this.board.metadata.updated_on, true);
    },
    deleted() {
      const deletedOn = get(this, 'board.metadata.deleted_on');
      if (!deletedOn) return this.$t('Deleted');

      return `${this.$t('Deleted')} ${formatDate(deletedOn, false)}`;
    },
    showBoardListingOptions() {
      return (this.sidebarFilter === 'TRASH' || this.board.state !== 'creation')
        && ['admin', 'owner'].includes(this.board.access_level);
    },
    type() {
      if (this.board.options.is_template && this.board.options.template_type === 'activity') {
        return this.board.options.type === 'board'
          ? this.$t('Board Activity Template')
          : this.$t('Slideshow Activity Template');
      }

      if (this.board.options.type === 'lesson') {
        return this.$t('Lesson');
      }

      return this.board.options.type === 'board' ? this.$t('Board') : this.$t('Slideshow');
    },
    thumbnail() {
      // If the board has a thumbnail of its own, use that. Otherwise use the thumbnail of the
      // first page, or fall back to the default thumbnail
      return get(this.board, 'thumbnail.url')
        || get(this.board, 'pages[0].thumbnail.url', defaultThumbnail);
    },
  },
};
</script>

<style lang="stylus">
.board-card {
  display: flex;
  min-height: 1px; // allow cards to resize in IE
  position: relative;

  &--list {
    border-radius: $nebula-border-radius-badge-default;
    margin: 0 0 $nebula-space-3x 0;
    width: 100%;

    .board-card__board-preview {
      flex-shrink: 0;
      height: 100%;
      padding: 0;
      width: 227px; // preserve aspect ratio of thumbnails
    }

    .board-card__board-link {
      box-shadow: $nebula-shadow-400;
      display: flex;
    }

    .board-card__body {
      flex-grow: 1;
      flex-shrink: 1;
      min-width: 0;
      padding: $nebula-space-2x 0 $nebula-space-2x;
    }

    .board-card__type {
      padding: 0 $nebula-space-6x 0 $nebula-space-3x;
    }

    .board-card__title {
      margin: 0 $nebula-space-6x $nebula-space-2x $nebula-space-3x;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .board-card__meta-wrapper {
      border-top: 1px solid $nebula-color-neutral-300;
      display: flex;
      padding: $nebula-space-2x $nebula-space-3x 0;
    }

    .board-card__meta {
      nebula-text-body-2();
      padding-right: $nebula-space-2x;
    }
  }

  &--grid {
    margin: 0 0 $nebula-space-4x;

    .board-card__board-preview {
      border-top-left-radius: var(--nebula-card-border-radius);
      border-top-right-radius: var(--nebula-card-border-radius);
      width: 100%;
    }
  }

  &__board-link {
    border-radius: $nebula-space-3x;
    box-shadow: $nebula-shadow-400;
    color: inherit;
    overflow: hidden;
    text-decoration: none;
    transition: transform $nebula-transition-default;
    width: 100%;

    &:hover,
    &:active,
    &:focus {
      .board-card__title {
        color: $nebula-color-interactive-blue-400;
      }
    }
  }

  &__board-preview {
    overflow: hidden;
    padding-top: 63%;
    position: relative;
  }

  &__board-preview-image {
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    bottom: 0;
    display: block;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    transition: transform $nebula-transition-default;
  }

  &__type {
    nebula-text-body-2();
    color: $nebula-color-interface-blue-500;
    font-size: $nebula-font-size-caption;
    font-weight: bold;
    margin: 0 0 $nebula-space-half;
    padding-right: $nebula-space-3x;
    text-transform: uppercase;
  }

  &__title {
    nebula-text-h6();
    font-size: var(--nebula-font-size-card-title);
    margin-bottom: $nebula-space-1x;
  }

  &__meta {
    nebula-text-body-1();
    margin: 0;

    span {
      font-weight: bold;
    }
  }

  html[dir="rtl"] & {
    &--list {
      .board-card__type {
        padding: 0 $nebula-space-3x 0 $nebula-space-6x;
      }

      .board-card__title {
        margin: 0 $nebula-space-3x $nebula-space-2x $nebula-space-6x;
      }

      .board-card__meta {
        padding-left: $nebula-space-2x;
        padding-right: 0;
      }
    }

    &__type {
      padding-left: $nebula-space-3x;
      padding-right: 0;
    }
  }
}
</style>
